import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { leadership, images, companyInfo } from '../data/mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { GraduationCap, Stethoscope, Award, Calendar, CheckCircle, Phone, Sparkles, Heart, Users, Globe, MapPin, ArrowRight } from 'lucide-react';

const ProvidersPage = () => {
  const leader = leadership[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero - Compact */}
      <section className="pt-6 pb-10 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={images.clinical} alt="Providers" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/80 to-[#0a1628]/60" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Our <span className="text-[#87CEEB]">Leadership</span></h1>
          <p className="text-base text-white/80">Meet our dedicated healthcare professionals.</p>
        </div>
      </section>

      {/* Our Clinical Research Site Section */}
      <section className="py-16 bg-gradient-to-b from-white to-[#F8FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center space-x-2 bg-[#1877F2]/10 text-[#1877F2] rounded-full px-4 py-2 mb-4">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-semibold">Our Clinical Research Site</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Welcome to <span className="text-[#1877F2]">C Doc Clinical Research Associates</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Provider Image - Properly Fitted */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                {/* Image Container with proper aspect ratio */}
                <div className="aspect-[4/5] w-full max-w-md mx-auto">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/80 via-transparent to-transparent" />
                
                {/* Provider Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="inline-block bg-[#1877F2] text-white text-xs font-bold px-3 py-1 rounded-full mb-2">
                    {leader.title}
                  </div>
                  <h3 className="text-xl font-bold">{leader.name}</h3>
                  <div className="flex items-center space-x-2 mt-2 text-white/80 text-sm">
                    <MapPin className="h-4 w-4" />
                    <span>Hyde Park, NY</span>
                  </div>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#1877F2]/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#87CEEB]/20 rounded-full blur-2xl" />
            </div>

            {/* AI-Generated Persuasive Biography */}
            <div className="space-y-6">
              <div className="prose prose-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Award className="h-6 w-6 text-[#1877F2] mr-2" />
                  Your Trusted Partner in Clinical Excellence
                </h3>
                
                <p className="text-gray-700 leading-relaxed">
                  <strong className="text-[#1877F2]">C Doc Clinical Research Associates</strong> stands at the forefront of clinical innovation in the Hudson Valley region. Founded by <strong>Dr. Christina M. Antoine, MD, MBA, MHSA, RRT</strong>, a distinguished board-certified internal medicine physician, our research site represents the gold standard in patient-centered clinical trials. What sets us apart is our <strong>unwavering commitment to participant safety</strong> and our ability to bridge the gap between groundbreaking scientific discovery and real-world patient care.
                </p>
              </div>

              {/* Key Differentiators */}
              <div className="grid grid-cols-1 gap-3 pt-4">
                {[
                  { icon: Heart, title: 'Patient-First', desc: 'Compassionate care always', id: 'patient-first' }
                ].map((item) => (
                  <div key={item.id} className="flex items-start space-x-3 p-3 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="w-10 h-10 bg-[#1877F2]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#1877F2]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">{item.title}</h4>
                      <p className="text-gray-600 text-xs">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3 pt-4">
                <Link to="/about" data-testid="about-site-learn-more-btn">
                  <Button className="bg-[#1877F2] hover:bg-[#166FE5] text-white px-6 py-3 rounded-full font-semibold">
                    Learn More About Us
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.contact.phone}`} data-testid="about-site-call-btn">
                  <Button variant="outline" className="border-[#1877F2] text-[#1877F2] px-6 py-3 rounded-full font-semibold">
                    <Phone className="mr-2 h-4 w-4" />
                    Call Now
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose - Compact */}
      <section className="py-8 bg-[#F8FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: Award, title: 'Board Certified', desc: 'Certified specialists', id: 'certified' },
              { icon: Stethoscope, title: 'Patient-Centered', desc: 'Your health first', id: 'patient-centered' },
              { icon: GraduationCap, title: 'Research Experts', desc: 'Years experience', id: 'experts' },
              { icon: Calendar, title: 'Accessible', desc: 'Easy scheduling', id: 'accessible' }
            ].map((item) => (
              <Card key={item.id} className="border-0 shadow-sm text-center p-3 rounded-xl">
                <div className="w-9 h-9 bg-[#1877F2] rounded-lg flex items-center justify-center mx-auto mb-2">
                  <item.icon className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm">{item.title}</h3>
                <p className="text-gray-600 text-xs">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Compact */}
      <section className="py-8 bg-gradient-to-r from-[#1877F2] to-[#0a1628]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-white mb-3">Schedule Your Appointment</h2>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <a href={`tel:${companyInfo.contact.phone}`} data-testid="providers-cta-call">
              <Button className="bg-white text-[#1877F2] px-6 py-3 rounded-full font-bold text-sm">
                <Phone className="mr-2 h-4 w-4" />
                {companyInfo.contact.phone}
              </Button>
            </a>
            <Link to="/contact" data-testid="providers-cta-contact">
              <Button variant="outline" className="border-2 border-white text-white px-6 py-3 rounded-full font-bold text-sm">Contact Online</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProvidersPage;
